import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";

export default function ProductDetail({ lang = "hr" }) {
  const { slug } = useParams();
  const [prod, setProd] = useState(null);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = {
    hr: {
      back: "Natrag na proizvode",
      from: "od",
      add: "Dodaj u košaricu",
      buy: "Kupi odmah",
      specs: "Specifikacije",
      size: "Veličina",
      height: "Visina",
      firmness: "Tvrdoća",
      core: "Jezgra",
      comfort: "Komfor slojevi",
      cover: "Navlaka",
      warranty: "Garancija",
      inStock: "Na zalihi",
      outStock: "Nema na zalihi",
      description: "Opis proizvoda",
      rating: "Ocjena",
    },
    en: {
      back: "Back to products",
      from: "from",
      add: "Add to cart",
      buy: "Buy now",
      specs: "Specifications",
      size: "Size",
      height: "Height",
      firmness: "Firmness",
      core: "Core",
      comfort: "Comfort layers",
      cover: "Cover",
      warranty: "Warranty",
      inStock: "In stock",
      outStock: "Out of stock",
      description: "Product description",
      rating: "Rating",
    },
  }[lang];

  // 🔹 Fallback demo
  const demo = useMemo(
    () => ({
      slug,
      title:
        lang === "hr"
          ? "Madrac " + (slug || "").toUpperCase()
          : "Mattress " + (slug || "").toUpperCase(),
      description_html:
        lang === "hr"
          ? "Premium džepičasti madrac s HR pjenama i luksuznom navlakom. Dizajnirano i proizvedeno u Hrvatskoj."
          : "Premium pocket-spring mattress with HR foams and a luxury cover. Designed and made in Croatia.",
      images: [
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1600&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1600&auto=format&fit=crop",
      ],
      price_from: 699,
      offers: [
        { size: "90x200", price: 499, availability: "InStock" },
        { size: "160x200", price: 699, availability: "InStock" },
        { size: "180x200", price: 799, availability: "InStock" },
      ],
      height: 25,
      firmness: "Medium-Firm",
      core: "Pocket spring 7 zona",
      comfort: "HR pjene visoke elastičnosti",
      cover: "Periva navlaka, antibakterijska",
      warrantyYears: 10,
      rating: { value: 4.8, count: 126 },
    }),
    [slug, lang]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await apiFetch(`/${lang}/products/${slug}`);
        const merged = {
          ...demo,
          ...data,
          title: data.title || demo.title,
          description_html: data.description_html || demo.description_html,
          price_from: data.price_from || demo.price_from,
        };
        if (alive) {
          setProd(merged);
          setSel(merged.offers?.[0] || null);
        }
      } catch {
        setProd(demo);
        setSel(demo.offers?.[0] || null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, [slug, lang]);

  if (loading || !prod) {
    return (
      <main className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="h-8 w-48 bg-white/10 rounded mb-6 animate-pulse" />
          <div className="grid md:grid-cols-2 gap-8">
            <div className="h-80 bg-white/10 rounded-2xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-white/10 rounded" />
              <div className="h-24 bg-white/10 rounded" />
              <div className="h-10 bg-white/10 rounded" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  const priceShown = sel?.price ?? prod.price_from;
  const canonical =
    lang === "hr"
      ? `${location.origin}/hr/proizvod/${prod.slug}`
      : `${location.origin}/en/product/${prod.slug}`;

  // 🔹 JSON-LD Product + Breadcrumb
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: prod.title,
    image: prod.images,
    description: prod.description_html,
    brand: { "@type": "Brand", name: "Somnia" },
    sku: prod.slug,
    aggregateRating: prod.rating && {
      "@type": "AggregateRating",
      ratingValue: prod.rating.value,
      reviewCount: prod.rating.count,
    },
    offers: (prod.offers || []).map((o) => ({
      "@type": "Offer",
      url: canonical,
      priceCurrency: "EUR",
      price: Number(o.price).toFixed(2),
      availability: `https://schema.org/${o.availability || "InStock"}`,
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Somnia Sleep" },
      sku: `${prod.slug}-${o.size}`,
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: lang === "hr" ? "Proizvodi" : "Products",
        item:
          lang === "hr"
            ? `${location.origin}/hr/proizvodi`
            : `${location.origin}/en/products`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: prod.title,
        item: canonical,
      },
    ],
  };

  return (
    <main className="bg-navy text-white">
      <section className="max-w-6xl mx-auto px-6 py-6 md:py-10">
        <a
          href={lang === "hr" ? "/hr/proizvodi" : "/en/products"}
          className="text-sm text-gray-300 hover:text-gold"
        >
          ← {t.back}
        </a>

        <div className="mt-6 grid md:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
              <img
                src={sel?.image || prod.images?.[0]}
                alt={prod.title}
                className="w-full h-80 object-cover"
              />
            </div>
            {prod.images?.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-3">
                {prod.images.map((img) => (
                  <button
                    key={img}
                    onClick={() => setSel((s) => ({ ...(s || {}), image: img }))}
                    className="h-20 rounded-xl overflow-hidden border border-white/10 hover:border-gold"
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info + CTA */}
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-gold">
              {prod.title}
            </h1>
            <p
              className="mt-3 text-gray-200"
              dangerouslySetInnerHTML={{ __html: prod.description_html }}
            />

            {/* Cijena + veličina */}
            <div className="mt-6 p-4 rounded-2xl border border-white/10 bg-white/5">
              <div className="text-sm text-gray-400">{t.from}</div>
              <div className="text-3xl font-semibold text-white">
                {Number(priceShown).toFixed(0)} €
              </div>

              <div className="mt-4">
                <div className="text-sm text-gray-300 mb-2">{t.size}</div>
                <div className="flex flex-wrap gap-2">
                  {(prod.offers || []).map((o) => {
                    const active = sel?.size === o.size;
                    const soldOut = o.availability === "OutOfStock";
                    return (
                      <button
                        key={o.size}
                        onClick={() => !soldOut && setSel(o)}
                        className={[
                          "h-10 px-3 rounded-lg border text-sm",
                          active
                            ? "border-gold text-gold"
                            : "border-white/20 text-white hover:border-gold",
                          soldOut ? "opacity-50 cursor-not-allowed" : "",
                        ].join(" ")}
                        title={soldOut ? t.outStock : t.inStock}
                      >
                        {o.size}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5 flex gap-3">
                  <button
                    className="inline-flex h-11 items-center rounded-full bg-gold px-5 font-semibold text-black hover:bg-gold-dark transition"
                    onClick={() => alert("TODO: add-to-cart")}
                  >
                    {t.add}
                  </button>
                  <button
                    className="inline-flex h-11 items-center rounded-full border border-white/20 px-5 font-semibold hover:border-gold hover:text-gold transition"
                    onClick={() => alert("TODO: checkout")}
                  >
                    {t.buy}
                  </button>
                </div>
              </div>
            </div>

            {/* Specifikacije */}
            <div className="mt-6">
              <h2 className="text-xl font-semibold">{t.specs}</h2>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Spec label={t.height} value={prod.height ? `${prod.height} cm` : "—"} />
                <Spec label={t.firmness} value={prod.firmness || "—"} />
                <Spec label={t.core} value={prod.core || "—"} />
                <Spec label={t.comfort} value={prod.comfort || "—"} />
                <Spec label={t.cover} value={prod.cover || "—"} />
                <Spec
                  label={t.warranty}
                  value={prod.warrantyYears ? `${prod.warrantyYears} g.` : "—"}
                />
                {prod.rating?.value && (
                  <Spec
                    label={t.rating}
                    value={`${prod.rating.value} / 5 (${prod.rating.count})`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Opis detaljnije */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold">{t.description}</h2>
          <p
            className="mt-3 text-gray-300"
            dangerouslySetInnerHTML={{ __html: prod.description_html }}
          />
        </div>
      </section>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />
    </main>
  );
}

function Spec({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-1 text-white">{value}</div>
    </div>
  );
}
